import { getTexts } from '@twnel/web-components';
import { SUCCESS_TYPE, ERROR_TYPE } from 'utils/toastDispacher';
import { toastDispacher } from 'utils';
import { getAllBotspecs } from '../selectors';
import { updateBotspecsVariable } from './spec_parser';

const GLOBAL_VARIABLES_GETTER = 'GLOBAL_VARIABLES_GETTER';
const GLOBAL_VARIABLE_GETTER = 'GLOBAL_VARIABLE_GETTER';
const GLOBAL_VARIABLE_CREATION = 'GLOBAL_VARIABLE_CREATION';
const GLOBAL_VARIABLE_UPDATE = 'GLOBAL_VARIABLE_UPDATE';
const GLOBAL_VARIABLE_DELETION = 'GLOBAL_VARIABLE_DELETION';

const networkError = (error) => {
  if (!(error instanceof Error)) {
    return new Error(error);
  }
  return { ...error, name: 'TwnelNetwork' };
};

const throwNetworkError = (error) => {
  throw networkError(error);
};

/**
 * Verifica si un botspec específico usa una variable global determinada
 */
const botspecUsesSpecificGlobalVariable = (botspec, variableName) => {
  if (!botspec || !botspec.spec_json) {
    return false;
  }
  const specJsonString = JSON.stringify(botspec.spec_json);
  const variableReference = `variables.global.${variableName}`;
  return specJsonString.includes(variableReference);
};

/**
 * Obtener todas las variables globales de la compañía
 */
export const fetchGlobalVariables = () => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.variables.list()
    .then((variables) => {
      // Convertir el array de variables a un objeto con el nombre como key
      const variablesObject = variables.reduce((acc, variable) => {
        acc[variable.name] = variable;
        return acc;
      }, {});
      return variablesObject;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_VARIABLES_GETTER, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

const updateBotspecsWithGlobalVariables = (variableName) => (dispatch, getState) => {
  const state = getState();
  const allBotspecs = getAllBotspecs(state);
  const botspecsToUpdate = Object.keys(allBotspecs)
    .map((key) => allBotspecs[key])
    .filter((botspec) => botspecUsesSpecificGlobalVariable(botspec, variableName));

  if (botspecsToUpdate.length === 0) {
    return Promise.resolve();
  }

  const updatePromises = botspecsToUpdate.map(
    (botspec) => dispatch(updateBotspecsVariable(botspec)),
  );
  return Promise.all(updatePromises).catch((error) => {
    // eslint-disable-next-line no-console
    console.error('Error updating botspecs with global variables:', error);
  });
};

/**
 * Obtener una variable global específica por nombre
 */
export const fetchGlobalVariable = (variableName) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.variables.get(variableName)
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_GETTER, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

/**
 * Crear una nueva variable global
 */
export const createGlobalVariable = (variableName, variableData) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.variables.create(variableName, variableData)
    .then((newVariable) => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_CREATION, texts, type: SUCCESS_TYPE });
      dispatch(updateBotspecsWithGlobalVariables(variableName));
      return newVariable;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_CREATION, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

/**
 * Actualizar una variable global existente
 */
export const updateGlobalVariable = (variableName, variableData) => (
  dispatch, getState, getContext,
) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.variables.update(variableName, variableData)
    .then((updatedVariable) => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_UPDATE, texts, type: SUCCESS_TYPE });
      dispatch(updateBotspecsWithGlobalVariables(variableName));
      return updatedVariable;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_UPDATE, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};

/**
 * Eliminar una variable global
 */
export const deleteGlobalVariable = (variableName) => (dispatch, getState, getContext) => {
  const { api } = getContext();
  const texts = getTexts(getState());

  return api.variables.delete(variableName)
    .then(() => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_DELETION, texts, type: SUCCESS_TYPE });
      dispatch(updateBotspecsWithGlobalVariables(variableName));
      return true;
    })
    .catch((error) => {
      toastDispacher({ actionId: GLOBAL_VARIABLE_DELETION, texts, type: ERROR_TYPE });
      throwNetworkError(error);
    });
};
